import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Eye, 
  ClipboardText, 
  Files, 
  ChatCircle,
  Clock,
  ArrowRight,
  CalendarBlank,
  Warning,
  CheckCircle
} from '@phosphor-icons/react';
import { Skeleton } from '../../components/ui/skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardAMO({ user }) {
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taches, setTaches] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [chantiersRes] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers`, { withCredentials: true }),
      ]);
      setChantiers(chantiersRes.data);
      
      // A6 (P2#12) — les actions de l'AMO (dont « Avis sur l'attribution demandé ») viennent
      // de « Mes actions » (GET /api/me/tasks) ; l'ancienne lecture des tâches par chantier
      // était refusée à l'AMO (message rouge « Permission insuffisante » à l'arrivée).
      const { data: mes } = await axios.get(`${API_URL}/api/me/tasks?etat=active`, { withCredentials: true, silent403: true })
        .catch(() => ({ data: { tasks: [] } }));
      setTaches((mes?.tasks || []).map((t) => ({ ...t, project_name: t.chantier_name, statut: t.etat === 'fait' ? 'terminee' : t.etat })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>;

  const activeTasks = taches.filter(t => t.statut !== 'terminee');
  const urgentTasks = activeTasks.filter(t => t.priorite === 'urgent');

  return (
    <div className="space-y-6" data-testid="dashboard-amo">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-[#2C2C2C]" style={{ fontFamily: "'DM Serif Display', serif" }}>
          Bonjour {user.name.split(' ')[0]}
        </h1>
        <p className="text-sm text-[#6B6B6B]">Synthèse de vos projets en cours</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-[#E0E0DB] bg-white p-4">
          <p className="text-2xl font-bold text-[#1E3A5F]">{chantiers.length}</p>
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Chantiers suivis</p>
        </div>
        <div className="border border-[#E0E0DB] bg-white p-4">
          <p className="text-2xl font-bold text-[#C75B39]">{urgentTasks.length}</p>
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Actions urgentes</p>
        </div>
        <div className="border border-[#E0E0DB] bg-white p-4">
          <p className="text-2xl font-bold text-[#2C2C2C]">{activeTasks.length}</p>
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Actions en cours</p>
        </div>
        <div className="border border-[#E0E0DB] bg-white p-4">
          <p className="text-2xl font-bold text-[#2D6A4F]">{taches.filter(t => t.statut === 'terminee').length}</p>
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Actions levées</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Projets suivis */}
        <div className="border border-[#E0E0DB] bg-white">
          <div className="px-4 py-3 border-b border-[#E0E0DB] flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">Mes projets</h2>
            <Eye size={16} className="text-[#6B6B6B]" />
          </div>
          <div className="divide-y divide-[#E0E0DB]">
            {chantiers.map(c => (
              <Link key={c.id} to={`/chantiers/${c.id}`} className="block px-4 py-3 hover:bg-[#FAFAF9] transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#2C2C2C]">{c.name}</p>
                    <p className="text-xs text-[#6B6B6B]">{c.address}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-[#E0E0DB] overflow-hidden">
                      <div className="h-full bg-[#1E3A5F]" style={{ width: `${c.progress}%` }} />
                    </div>
                    <span className="text-xs text-[#6B6B6B]">{c.progress}%</span>
                    <ArrowRight size={14} className="text-[#9A9A9A]" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Tâches prioritaires */}
        <div className="border border-[#E0E0DB] bg-white">
          <div className="px-4 py-3 border-b border-[#E0E0DB]">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">Tâches prioritaires</h2>
          </div>
          <div className="divide-y divide-[#E0E0DB]">
            {activeTasks.slice(0, 6).map(t => (
              <div key={t.id} className="px-4 py-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#2C2C2C]">{t.titre}</p>
                    <p className="text-xs text-[#6B6B6B] mt-0.5">{t.project_name}</p>
                  </div>
                  {t.priorite === 'urgent' && (
                    <span className="text-xs px-2 py-0.5 bg-[#FFEBEE] text-[#8B3A3A] font-bold">URGENT</span>
                  )}
                </div>
                {t.echeance && (
                  <p className="text-xs text-[#9A9A9A] mt-1 flex items-center gap-1">
                    <Clock size={10} />
                    {new Date(t.echeance).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            ))}
            {activeTasks.length === 0 && <p className="text-sm text-[#9A9A9A] text-center py-6">Aucune tâche en cours</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
